/**
 * A TELEPÍTÉS LEHETŐSÉGE — a böngésző `beforeinstallprompt` eseménye, eltéve.
 *
 * MIÉRT MODUL-SZINTŰ TÁROLÓ, ÉS NEM EGY HOOK `useEffect`-je. A Chrome az
 * eseményt az oldal betöltése után egyszer süti el, gyakran MÉG AZELŐTT, hogy
 * a React felállna. Egy hookban felcsatolt figyelő ilyenkor lekési, és a gomb
 * soha nem jelenik meg — pont a „miért nincs Install gomb" hiba. Ezért a
 * figyelő a `main.tsx`-ben indul, a render ELŐTT, és ide teszi el az eseményt.
 *
 * Az iOS Safari ilyen eseményt NEM küld (és nem is fog): ott a telepítés a
 * Megosztás menüből megy, és a sáv csak elmondja, hogyan (`InstallBanner`).
 *
 * Ugyanez a minta él az eventbotban (`hooks/usePWAInstall.ts`) — ott egy
 * fejléc-script kapja el az eseményt, itt a belépési modul.
 */

/** A Chrome eseménye — a DOM típusai között nincs benne. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface InstallState {
  /** A böngésző felkínálja a telepítést, és mi tartjuk a kezünkben. */
  canPrompt: boolean;
  /** Ebben a munkamenetben épp most telepítették. */
  installed: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;
let state: InstallState = { canPrompt: false, installed: false };
let listening = false;
const listeners = new Set<() => void>();

function setState(next: InstallState): void {
  state = next;
  for (const listener of listeners) {
    listener();
  }
}

/** A render előtt hívandó, egyszer. A második hívás nem csatol új figyelőt. */
export function listenForInstallPrompt(): void {
  if (listening) {
    return;
  }
  listening = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    // A mini-infobart a saját sávunk váltja ki — kettő ne álljon egyszerre.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    setState({ ...state, canPrompt: true });
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    setState({ canPrompt: false, installed: true });
  });
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getInstallState(): InstallState {
  return state;
}

/**
 * A böngésző saját telepítő-párbeszéde. Egy eseményt CSAK EGYSZER lehet
 * elsütni — akár elfogadta, akár elutasította, utána eldobjuk, és a gomb
 * eltűnik. Elutasítás után a Chrome később újra küldhet eseményt.
 */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (event === null) {
    return false;
  }
  deferred = null;
  setState({ ...state, canPrompt: false });
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === 'accepted';
}

/** Csak a teszteknek: tiszta lap minden eset előtt. */
export function resetInstallPromptForTests(): void {
  deferred = null;
  state = { canPrompt: false, installed: false };
  listeners.clear();
}
